"use client";

import React from "react";
import { MapPin, Phone, Mail, Terminal, ShieldAlert, Cpu } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
const contactInfo = [{
  icon: MapPin,
  label: "Localização",
  value: "Rua Cláudio Soares, 72 - Conj. 1406\nPinheiros, São Paulo - SP",
  description: "Próximo à Faria Lima"
}, {
  icon: Phone,
  label: "WhatsApp",
  value: "(11) 96910-0007",
  description: "Atendimento Comercial"
}, {
  icon: Mail,
  label: "E-mail",
  value: "contato@itcmais.com",
  description: "Solicite um orçamento"
}];
const inputBaseClasses = "flex w-full bg-transparent border-0 border-b border-border py-3 text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-[0_2px_10px_-2px_var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 rounded-none font-default";
function ExpeditionFormInternal() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="225268" className="flex flex-col items-center justify-center p-8 text-center border border-primary/30 bg-primary/5 rounded-lg shadow-lg">
        <ShieldAlert className="size-12 text-primary mb-4" />
        <h3 className="text-2xl font-default font-bold text-foreground mb-2">
          Mensagem Enviada
        </h3>
        <p className="text-muted-foreground font-default mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-default border-primary/50 hover:bg-primary/10 hover:text-primary rounded-full">
          Enviar Nova Mensagem
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6">
        <FormField control={control} name="full_name" rules={{
        required: "Nome é obrigatório."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-primary tracking-wider uppercase text-xs">
                Nome Completo
              </FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Seu nome ou empresa" className={inputBaseClasses} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                Como devemos chamar você?
              </FormDescription>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "E-mail é obrigatório.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Formato de e-mail inválido."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-primary tracking-wider uppercase text-xs">
                E-mail Corporativo
              </FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="exemplo@empresa.com.br" className={inputBaseClasses} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                Enviaremos a proposta para este endereço.
              </FormDescription>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="age" rules={{
          min: {
            value: 1,
            message: "Valor inválido."
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-primary tracking-wider uppercase text-xs">
                  Telefone / WhatsApp
                </FormLabel>
                <FormControl>
                  <input {...field} type="text" placeholder="(11) 99999-9999" className={inputBaseClasses} />
                </FormControl>
                <FormDescription className="font-default text-xs opacity-70">
                  Para contato rápido.
                </FormDescription>
                <FormMessage className="font-default text-xs" />
              </FormItem>} />

          <FormField control={control} name="education_level" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-primary tracking-wider uppercase text-xs">
                  Serviço de Interesse
                </FormLabel>
                <FormControl>
                  <select {...field} className={`${inputBaseClasses} appearance-none cursor-pointer`}>
                    <option value="" disabled className="bg-background text-muted-foreground">
                      Selecione...
                    </option>
                    {["Produção de Vídeo", "Estúdio de Podcast", "Tráfego Pago", "SEO & Conteúdo", "Outros"].map((option, i) => <option key={i} value={option} className="bg-background text-foreground">
                          {option}
                        </option>)}
                  </select>
                </FormControl>
                <FormDescription className="font-default text-xs opacity-70">
                  Qual solução você busca?
                </FormDescription>
                <FormMessage className="font-default text-xs" />
              </FormItem>} />
        </div>

        <FormField control={control} name="interest_message" rules={{
        maxLength: {
          value: 500,
          message: "Mensagem muito longa."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-primary tracking-wider uppercase text-xs">
                Detalhes do Projeto
              </FormLabel>
              <FormControl>
                <textarea {...field} placeholder="Conte-nos brevemente sobre sua necessidade..." className={`${inputBaseClasses} min-h-[120px] resize-none`} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 rounded-md">
          <p className="text-destructive font-default text-sm flex items-center gap-2">
            <Terminal className="size-4" />
            Erro no sistema: {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 font-default text-lg tracking-widest uppercase relative overflow-hidden group bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300 rounded-full">
        <span className="relative z-10 flex items-center gap-2">
          {isSubmitting ? <>
              <Cpu className="animate-spin size-5" />
              Enviando...
            </> : <>
              <Terminal className="size-5" />
              Iniciar Projeto
            </>}
        </span>
        <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out" />
      </Button>
    </Form>;
}
export default function JoinTheExpedition() {
  return <section id="vamos-iniciar-seu-projeto" className="bg-background text-foreground py-[var(--section-vertical-padding)] px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
        <div className="absolute left-[10%] top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/30 to-transparent" />
        <div className="absolute right-[10%] top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/30 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-24 items-start">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary font-mono text-sm mb-6">
                <ShieldAlert className="size-4" />
                <span>Disponível para novos projetos</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
                Vamos Iniciar seu Projeto
              </h2>
              <p className="text-muted-foreground font-default text-lg leading-relaxed">
                Pronto para posicionar sua empresa com autoridade? Fale com nossos especialistas em audiovisual e estratégias de conversão em São Paulo.
              </p>
            </div>

            <div className="space-y-8">
              {contactInfo.map((item, index) => <div key={index} data-index={index} className="flex items-start gap-4 group">
                  <div className="p-3 rounded-full bg-primary/5 border border-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground shadow-sm transition-all duration-300">
                    <item.icon className="size-6" />
                  </div>
                  <div>
                    <h4 className="font-mono text-sm text-primary tracking-wider uppercase mb-1">
                      {item.label}
                    </h4>
                    <p className="text-lg font-semibold text-foreground mb-1">
                      {item.value}
                    </p>
                    <p className="text-sm text-muted-foreground font-default">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-card/30 backdrop-blur-sm border border-border p-8 md:p-12 rounded-3xl relative shadow-xl">
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary/30 rounded-tl-3xl" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-primary/30 rounded-tr-3xl" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-primary/30 rounded-bl-3xl" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-primary/30 rounded-br-3xl" />

              <div className="mb-8 border-b border-border/50 pb-6">
                <h3 className="text-2xl font-default font-semibold text-foreground flex items-center gap-3">
                  <Terminal className="size-6 text-primary" />
                  Solicitar Orçamento
                </h3>
                <p className="text-muted-foreground font-default text-sm mt-2">
                  Preencha os dados abaixo para receber uma proposta personalizada.
                </p>
              </div>

              <FormProvider formKey="start_project_form_contact" formId="start_project_form_contact" sectionName="Vamos Iniciar seu Projeto" defaultValues={{
              full_name: "",
              email_address: "",
              age: "",
              education_level: "",
              interest_message: ""
            }} submitText="Iniciar Projeto" successMessage="Sua mensagem foi recebida com sucesso. Nossa equipe entrará em contato em breve para alinhar os detalhes.">
                <ExpeditionFormInternal />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}